// Model-agnostic parsed row: turning raw spreadsheet objects into typed, validated
// rows for any ImportModelDescriptor.
//
// Design note - why values are stored as DISPLAY values, not resolved entities:
// the review grid is a spreadsheet-style editor, so every cell has to round-trip
// through a string the user can type or paste. Entity and enum resolution therefore
// happens in validateImportRow (and again at submit) rather than at parse time. That
// keeps one resolution path instead of two, and means an edited cell revalidates the
// same way an imported one did.
import {
    BudgetPricingStrategyModel,
    GetCustomerDataResponse,
    GetCustomerSettingsResponse,
} from '../../../../Generated/Commodity-Management-Api';
import {
    findBestEntityMatch,
    findMatchingPricingStrategies,
    matchEnumValue,
    months,
} from '../../../../Helpers';
import { FieldMappings, getMappedColumnsByField } from './ColumnMapping';
import {
    parseDateValue,
    parseMonthValue,
    parseNumberValue,
    parseYearValue,
} from './ImportValueParsers';
import {
    ImportEntitySource,
    ImportFieldDescriptor,
    ImportModelDescriptor,
    getActiveFields,
} from './ImportModelRegistry';

export interface FieldIssue {
    field: string;
    message: string;
}

export interface ImportRow {
    id: string;
    /** 1-based row number in the source sheet, including the header row. */
    rowNumber: number;
    sheetName?: string;
    /** Field key -> display value, as shown and edited in the review grid. */
    values: Record<string, unknown>;
    /** Columns that were not mapped, kept so the grid can show them read-only. */
    unmapped: Record<string, unknown>;
}

export interface ImportContext {
    customerData: GetCustomerDataResponse | undefined;
    customerSettings: GetCustomerSettingsResponse | undefined;
    pricingStrategies?: BudgetPricingStrategyModel[];
    siteGuid?: string;
}

export interface ValidatedImportRow {
    row: ImportRow;
    errors: FieldIssue[];
    warnings: FieldIssue[];
    /** Field key -> resolved entity object, enum value, or parsed primitive. */
    resolved: Record<string, unknown>;
}

const ENTITY_NAME_FIELDS: Record<ImportEntitySource, string> = {
    commodities: 'commodityName',
    counterparties: 'counterpartyName',
    locations: 'locationName',
    portfolios: 'portfolioName',
    budgetRounds: 'name',
    budgetPricingStrategies: 'name',
};

function getEntityCollection(
    source: ImportEntitySource,
    ctx: ImportContext
): Record<string, any>[] {
    switch (source) {
        case 'commodities': return ctx.customerData?.commodities ?? [];
        case 'counterparties': return ctx.customerData?.counterparties ?? [];
        case 'locations': return ctx.customerData?.locations ?? [];
        case 'portfolios': return ctx.customerData?.portfolios ?? [];
        case 'budgetRounds': return ctx.customerData?.budgetRounds ?? [];
        case 'budgetPricingStrategies': return ctx.pricingStrategies ?? [];
        default: return [];
    }
}

export function getEntityLabel(source: ImportEntitySource, entity: Record<string, any> | undefined): string {
    if (!entity) return '';
    return String(entity[ENTITY_NAME_FIELDS[source]] ?? '');
}

/**
 * Build rows from raw sheet objects using the resolved field mappings. Only columns
 * some field maps to become values; everything else is retained in `unmapped` so the
 * grid can still show it.
 *
 * Two fields may name the same column - that is how one date column fills both the
 * month and the year of a date group, each normalizing out the part it needs.
 */
export function buildImportRows(
    rawRows: Record<string, unknown>[],
    mappings: FieldMappings,
    model: ImportModelDescriptor,
    ctx: ImportContext,
    options: { sheetName?: string; startRowNumber?: number } = {}
): ImportRow[] {
    const columnByField = getMappedColumnsByField(mappings);
    const activeFields = getActiveFields(model, ctx.customerSettings);
    const startRowNumber = options.startRowNumber ?? 2;

    const usedColumns = new Set(Object.values(columnByField));

    return rawRows.map((raw, index) => {
        const values: Record<string, unknown> = {};

        for (const field of activeFields) {
            if (field.derived) continue;
            const column = columnByField[field.key];
            values[field.key] = normalizeFieldValue(
                field,
                column === undefined ? undefined : raw[column],
                ctx
            );
        }

        const unmapped: Record<string, unknown> = {};
        for (const [column, value] of Object.entries(raw)) {
            if (usedColumns.has(column)) continue;
            unmapped[column] = value;
        }

        return {
            id: `${options.sheetName ?? 'sheet'}-${startRowNumber + index}-${index}`,
            rowNumber: startRowNumber + index,
            sheetName: options.sheetName,
            values,
            unmapped,
        };
    });
}

/**
 * Coerce a raw cell into the display form the grid edits. Entity and enum fields keep
 * a string (resolved later); numeric/date fields are normalized so the grid shows
 * consistent values regardless of how the file expressed them.
 */
function normalizeFieldValue(
    field: ImportFieldDescriptor,
    value: unknown,
    ctx: ImportContext
): unknown {
    if (value === undefined || value === null || value === '') return undefined;

    switch (field.kind) {
        case 'number':
            return parseNumberValue(value);
        case 'month': {
            // Stored as the month NAME so the review grid can use a dropdown, matching
            // EditBudgetsTable. parseMonthValue accepts names and numbers, so a user
            // typing or pasting "5" still validates.
            const month = parseMonthValue(value);
            return month ? months[month - 1] : String(value);
        }
        case 'year':
            return parseYearValue(value);
        case 'date': {
            const parsed = parseDateValue(value);
            return parsed ? parsed.format('YYYY-MM-DD') : String(value);
        }
        case 'enum': {
            const matched = field.enumValues ? matchEnumValue(value, field.enumValues) : undefined;
            return matched ?? String(value);
        }
        case 'entity': {
            // Snap to the canonical entity name when we can, so the grid shows the
            // site's spelling rather than the file's.
            const source = field.entitySource;
            if (!source) return String(value);
            const collection = getEntityCollection(source, ctx);
            const { item } = findBestEntityMatch(value, collection, ENTITY_NAME_FIELDS[source]);
            return item ? getEntityLabel(source, item) : String(value);
        }
        default:
            return String(value);
    }
}

/**
 * Derive fields the file never supplies. Currently only a budget's pricing strategy,
 * which comes from commodity + location + counterparty.
 *
 * Returns the matching strategies too, so an ambiguous derivation can offer the user
 * the real choices instead of silently failing (the legacy importer collapses this to
 * null and then dereferences it).
 */
export function derivePricingStrategy(
    row: ImportRow,
    ctx: ImportContext
): { strategy: BudgetPricingStrategyModel | undefined; candidates: BudgetPricingStrategyModel[] } {
    const commodity = resolveEntity('commodities', row.values.commodity, ctx);
    const location = resolveEntity('locations', row.values.location, ctx);
    const counterparty = resolveEntity('counterparties', row.values.counterparty, ctx);

    const candidates = findMatchingPricingStrategies(ctx.pricingStrategies, {
        commodityId: commodity?.commodityId,
        locationId: location?.locationId,
        counterpartyId: counterparty?.counterpartyId,
    });

    return { strategy: candidates.length === 1 ? candidates[0] : undefined, candidates };
}

export function resolveEntity(
    source: ImportEntitySource,
    value: unknown,
    ctx: ImportContext
): Record<string, any> | undefined {
    if (value === undefined || value === null || value === '') return undefined;
    const collection = getEntityCollection(source, ctx);
    return findBestEntityMatch(value, collection, ENTITY_NAME_FIELDS[source]).item;
}

/**
 * Validate one row against its model. Errors block upload; warnings do not.
 * Required-ness comes from the field descriptors rather than a hardcoded list, so a
 * new model gets validation for free.
 */
export function validateImportRow(
    row: ImportRow,
    model: ImportModelDescriptor,
    ctx: ImportContext
): ValidatedImportRow {
    const errors: FieldIssue[] = [];
    const warnings: FieldIssue[] = [];
    const resolved: Record<string, unknown> = {};

    for (const field of getActiveFields(model, ctx.customerSettings)) {
        const value = row.values[field.key];

        if (field.derived) {
            if (field.key === 'pricingStrategy') {
                const explicit = value
                    ? resolveEntity('budgetPricingStrategies', value, ctx)
                    : undefined;
                const { strategy, candidates } = derivePricingStrategy(row, ctx);
                const chosen = explicit ?? strategy;

                if (chosen) {
                    resolved[field.key] = chosen;
                } else if (candidates.length > 1) {
                    errors.push({
                        field: field.key,
                        message: `${candidates.length} pricing strategies match - choose one`,
                    });
                } else {
                    errors.push({ field: field.key, message: 'No matching pricing strategy' });
                }
            }
            continue;
        }

        const isEmpty = value === undefined || value === null || value === '';
        if (isEmpty) {
            if (field.required) {
                errors.push({ field: field.key, message: `${field.label} is required` });
            } else if (field.key === 'price') {
                // Historically a warning rather than an error - a position with no price
                // is legitimate and should still upload.
                warnings.push({ field: field.key, message: 'No price' });
            }
            continue;
        }

        switch (field.kind) {
            case 'entity': {
                const source = field.entitySource!;
                const entity = resolveEntity(source, value, ctx);
                if (!entity) {
                    errors.push({
                        field: field.key,
                        message: `"${String(value)}" is not a known ${field.label.toLowerCase()}`,
                    });
                } else {
                    resolved[field.key] = entity;
                }
                break;
            }
            case 'enum': {
                const matched = field.enumValues ? matchEnumValue(value, field.enumValues) : undefined;
                if (!matched) {
                    errors.push({
                        field: field.key,
                        message: `"${String(value)}" is not a valid ${field.label.toLowerCase()}`,
                    });
                } else {
                    resolved[field.key] = matched;
                }
                break;
            }
            case 'number': {
                const parsed = parseNumberValue(value);
                if (parsed === undefined) {
                    errors.push({ field: field.key, message: `${field.label} must be a number` });
                } else {
                    resolved[field.key] = parsed;
                }
                break;
            }
            case 'month': {
                const parsed = parseMonthValue(value);
                if (parsed === undefined) {
                    errors.push({ field: field.key, message: `${field.label} must be a month (1-12)` });
                } else {
                    resolved[field.key] = parsed;
                }
                break;
            }
            case 'year': {
                const parsed = parseYearValue(value);
                if (parsed === undefined) {
                    errors.push({ field: field.key, message: `${field.label} must be a year` });
                } else {
                    resolved[field.key] = parsed;
                }
                break;
            }
            case 'date': {
                const parsed = parseDateValue(value);
                if (!parsed) {
                    errors.push({ field: field.key, message: `${field.label} must be a date` });
                } else {
                    resolved[field.key] = parsed.format('YYYY-MM-DD');
                }
                break;
            }
            default:
                resolved[field.key] = String(value);
        }
    }

    return { row, errors, warnings, resolved };
}

// Re-exported for call sites that already reach for these here; they live in
// ImportValueParsers.ts now.
export { parseDateValue, parseMonthValue, parseNumberValue, parseYearValue };
